import { NavLink, useLocation, useNavigate } from 'react-router'
import { clsx } from 'clsx'
import { Fragment } from 'react'

export type Page = {
  title: string
  to: string
  // Sibling paths this entry also owns. An entry that fronts a tabbed page has to stay
  // highlighted while the reader is on one of the other tabs, which NavLink will not do on its
  // own because it only matches its own path.
  alsoActiveOn?: string[]
} | null

interface NavMenuProps {
  icon: JSX.Element
  title: string
  isActive?: boolean
  pages?: Page[]
}

export const NavMenu: React.FC<NavMenuProps> = ({
  icon,
  title,
  isActive,
  pages
}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // The section header navigates on click rather than through a link, so it announces its target
  // for a page guarding unsaved changes to intercept.
  const mainNavTarget = isActive ? undefined : pages?.find((p) => p)?.to

  const handleClickMainNavItem = () => {
    if (mainNavTarget) navigate(mainNavTarget)
  }

  return (
    <div>
      <li
        className={clsx(
          'flex cursor-pointer items-center justify-between gap-2 rounded-lg p-2',
          'fill-[#99A0AE] text-[#99A0AE] hover:fill-white hover:text-white'
        )}
        onClick={handleClickMainNavItem}
        data-nav-to={mainNavTarget}
      >
        <div className={clsx('flex items-center gap-2')}>
          <div className={clsx(isActive && 'text-[#098FF9]')}>{icon}</div>
          <p className={clsx('text-sm')}>{title}</p>
        </div>
      </li>
      {pages?.length ? (
        <div
          className={clsx(
            'grid grid-rows-[0fr] transition-all duration-300',
            isActive && 'grid-rows-[1fr]'
          )}
        >
          <ul
            className={clsx(
              'relative flex flex-col gap-1 overflow-hidden pl-8',
              isActive && 'p-1'
            )}
          >
            {pages.map((s, i) => {
              if (!s) return null
              return (
                <Fragment key={s.title}>
                  <NavLink to={s.to}>
                    {({ isActive }) => {
                      const isCurrent =
                        isActive ||
                        (s.alsoActiveOn?.some((path) =>
                          pathname.startsWith(path)
                        ) ??
                          false)
                      return (
                        <li
                          className={clsx(
                            'relative flex items-center gap-2 rounded-lg p-2',
                            'fill-[#99A0AE] text-[#99A0AE]',
                            'hover:bg-[#2C3242] hover:outline hover:outline-1 hover:outline-[#353C4E]',
                            isCurrent &&
                              'bg-[#2C3242] text-white outline outline-1 outline-[#353C4E]'
                          )}
                        >
                          <span
                            className={clsx(
                              'absolute -left-px h-3/5 w-[3px] rounded-r-full bg-blue-500',
                              !isCurrent && 'hidden'
                            )}
                          />

                          <span
                            className={clsx(
                              `absolute left-[-16px] w-px bg-[#282E3B]`
                            )}
                            style={{
                              top: `${(i + 1) * -40}px`,
                              height: isCurrent
                                ? `${10 + (i + 1) * 40}px`
                                : undefined
                            }}
                          />
                          <span
                            className={clsx(
                              'absolute left-[-16px] top-[4px] h-4 w-[15px]',
                              'rounded-bl-lg border-b border-l border-solid border-[#282E3B]',
                              !isCurrent && 'hidden'
                            )}
                          />
                          <p className="ml-1 truncate text-sm">{s.title}</p>
                        </li>
                      )
                    }}
                  </NavLink>
                </Fragment>
              )
            })}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
